import React from 'react';
import {Box, styled} from '@mui/material';
import {
  designSystemToken,
  Headline,
  Icon,
  IconName,
  Label,
} from '@lightricks/react-design-system';
import {FeatureName} from '@contentlab/types/project';
import getFeatureIconName from '@contentlab/utils/getFeatureIconName';
import translate from '@/utils/translate';

export interface MinimalSelectionItem {
  id: FeatureName;
  title: string;
}

interface MinimalSelectionButtonProps {
  item: MinimalSelectionItem;
  onClick: () => void;
  isSelected: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  isPopular?: boolean;
}

const shouldNotForwardProps = ['isSelected'];

const PopularTagWrapper = styled(Box)(() => ({
  position: 'absolute',
  top: '9px',
  left: '14px',
}));

function IsPopularTag() {
  return (
    <Label
      size="xs"
      sx={{
        padding: '0 4px',
        borderRadius: '4px',
        backgroundColor: designSystemToken('semantic.bg.neutral-inverse'),
      }}
    >
      {translate('Popular')}
    </Label>
  );
}

const ButtonContainer = styled(Box, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})(({isSelected}: {isSelected: boolean}) => ({
  'display': 'flex',
  'flexDirection': 'column',
  'cursor': 'pointer',
  'maxHeight': '156px',
  'border': `1px solid ${isSelected ? designSystemToken('semantic.fg.primary') : designSystemToken('semantic.bg.neutral-inverse')}`,
  'padding': '32px 16px',
  'borderRadius': '24px',
  'alignItems': 'center',
  'justifyContent': 'center',
  'gap': '20px',
  'overflow': 'hidden',
  'boxSizing': 'border-box',
  'position': 'relative',
  '&:hover': {
    backgroundColor: designSystemToken('semantic.bg.tertiary'),
  },
}));

const IconWrapper = styled(Box)(() => ({
  position: 'relative',
}));

const IconContainer = styled(Box)(() => ({
  width: '48px',
  height: '48px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: designSystemToken('semantic.bg.brand-secondary'),
  borderRadius: '13.617px',
  border: `0.851px solid ${designSystemToken('semantic.bg.alpha')}`,
}));

const Title = styled(Headline)(() => ({
  textAlign: 'center',
  whiteSpace: 'pre-line',
  color: designSystemToken('semantic.fg.primary'),
}));

function MinimalSelectionButton({
  item,
  onClick,
  isSelected,
  onMouseEnter,
  onMouseLeave,
  isPopular = false,
}: MinimalSelectionButtonProps) {
  const iconName = getFeatureIconName(item.id) as IconName;

  return (
    <ButtonContainer
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      isSelected={isSelected}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick();
        }
      }}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {isPopular ? (
        <PopularTagWrapper>
          <IsPopularTag />
        </PopularTagWrapper>
      ) : null}
      <IconWrapper>
        <IconContainer>
          <Icon
            size="medium"
            appearance="neutral"
            name={iconName}
            color={designSystemToken('semantic.fg.primary')}
          />
        </IconContainer>
      </IconWrapper>
      <Title size="xxs">{translate(item.title)}</Title>
    </ButtonContainer>
  );
}

export default MinimalSelectionButton;
